/**
 * Slot-aware embed resolution.
 *
 * The PDF generator used to embed every photo at up to MAX_IMAGE_LONG_EDGE
 * (3000 px) on the long edge regardless of how big the photo actually appears
 * on the printed page. A photo dropped into a small grid cell (e.g. a 4-up
 * quadrant) is only ~100 × 70 mm on paper — at 300 dpi that needs ~1200 px,
 * not 3000. Embedding the full 3000 px there just bloats the PDF (a 117-photo
 * album hit ~46 MB and blew past the Supabase bucket limit → HTTP 400).
 *
 * This module computes the long-edge pixel count a given slot ACTUALLY needs
 * at 300 dpi, so each draw site can downscale to that instead of the global
 * cap. It is resolution-only: it never touches draw geometry. A full-page
 * 1-up still resolves to MAX_IMAGE_LONG_EDGE, so print quality there is
 * unchanged.
 */
import { PRINT_MAX_DIM } from './imageLimits';

// Soft cap on embedded image dimensions. Larger originals are downsampled to
// this on the long edge.
//
// MUST STAY EQUAL TO PRINT_MAX_DIM (lib/imageLimits.ts). When the embed cap
// matches what intake stores, a stored original needs no downscale at a
// full-page slot, so normaliseImageBlob takes its verbatim-passthrough branch
// (pdfGenerator.ts:654) and the photo is embedded byte-for-byte with ZERO
// re-encode. Setting this BELOW the intake cap silently re-compresses every
// full-page photo at the tier quality (0.78) — that was the pre-2026-07-29
// behaviour, which printed full pages at 251 dpi.
export const MAX_IMAGE_LONG_EDGE = PRINT_MAX_DIM;

// PDF page units are points. A4_W = 210 mm × 2.8346 pt/mm ≈ 595.27 pt, and
// 210 mm = 8.2677 in → 595.27 / 8.2677 ≈ 72 pt/in. So one page unit is a
// point and there are exactly 72 of them per inch.
export const PAGE_UNITS_PER_INCH = 72;

// Print resolution we target. Storiz prints hardcover at 300 dpi.
export const PRINT_DPI = 300;

// Floor so even a tiny decorative slot still embeds enough pixels to not look
// soft if the customer later zooms a little. 700 px ≈ a 2.3 in cell at 300dpi.
export const MIN_SLOT_LONG_EDGE = 700;

// Extra margin on top of the exact 300dpi requirement. Covers cover-fit
// overscan (the image is scaled to COVER the slot, so part of it is cropped —
// the drawn dimension can exceed the slot) plus a little anti-aliasing
// headroom so downscaling never visibly softens the print.
export const RESOLUTION_HEADROOM = 1.15;

/**
 * Long-edge pixel count a slot needs at 300 dpi, accounting for the user's
 * zoom (a zoomed-in crop shows fewer source pixels across the slot, so it
 * needs MORE source resolution to stay sharp) and a fixed headroom factor.
 *
 * @param slotW slot width in page units (PDF points)
 * @param slotH slot height in page units (PDF points)
 * @param userScale customer's saved zoom for the slot (default 1; values < 1
 *   are clamped to 1 so a zoomed-OUT slot never under-resolves)
 * @param maxLongEdge upper cap (defaults to MAX_IMAGE_LONG_EDGE)
 * @returns integer long-edge px, clamped to [MIN_SLOT_LONG_EDGE, maxLongEdge]
 */
export function computeSlotTargetLongEdge(
  slotW: number,
  slotH: number,
  userScale = 1,
  maxLongEdge: number = MAX_IMAGE_LONG_EDGE,
): number {
  const slotLongEdge = Math.max(slotW, slotH);
  // Guard against degenerate / non-finite slot dims — fall back to the cap so
  // we never silently embed a 1px image.
  if (!Number.isFinite(slotLongEdge) || slotLongEdge <= 0) {
    return Math.round(maxLongEdge);
  }
  const pxPerUnit = PRINT_DPI / PAGE_UNITS_PER_INCH; // 300 / 72 ≈ 4.1667
  const effectiveScale = Math.max(userScale, 1);
  const needed = slotLongEdge * pxPerUnit * effectiveScale * RESOLUTION_HEADROOM;
  const clamped = Math.min(Math.max(needed, MIN_SLOT_LONG_EDGE), maxLongEdge);
  return Math.round(clamped);
}

/** PRINTER UPGRADE (design system step 2) — a free photo ELEMENT (the torn-edge
 *  photo, a polaroid's photo…) gets the same 300 dpi budget as a slot. It used
 *  to be re-encoded at the source's full size: a 3600 px original for an 80 mm
 *  polaroid window weighed ~2 MB instead of ~0.5 MB, and 9 decorated pages
 *  pushed a 36-page album past 50 MB. Never enlarges. */
export function elementPhotoPixels(srcW: number, srcH: number, boxWPt: number, boxHPt: number): { w: number; h: number } {
  const target = computeSlotTargetLongEdge(boxWPt, boxHPt, 1);
  const s = Math.min(1, target / Math.max(srcW, srcH));
  return { w: Math.max(1, Math.round(srcW * s)), h: Math.max(1, Math.round(srcH * s)) };
}
